import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import ForkliftIcon from "@mui/icons-material/Forklift";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import Box from "@mui/material/Box";
import type React from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { formatStatusLabel } from "@/components/Dispatch/format";
import MdiHardHat from "@/components/Icons/MdiHardHat";
import MdiSleep from "@/components/Icons/MdiSleep";
import MdiSteering from "@/components/Icons/MdiSteering";
import { HOS_CLOCK_ORDER } from "@/hooks/dispatch/driverHosSummary";
import { DRIVER_DUTY_STATUS_FILTERS, type DriverDutyStatusFilter, type DriverRow } from "@/types/driver";
import type { DriverHosSummary, HosClockSummary } from "@/types/driverHos";
import { zinc } from "@/ui/tokens";
import { CONTEXT_ACCENT, ContextCaption, ContextField, ContextSection, ContextText } from "./ContextBlock";
import { changedAt, NO_VALUE } from "./contextFormat";
import { formatHosRemaining, formatRecap, HOS_CLOCK_LABELS } from "./hosFormat";

/**
 * The icon each duty status reads with beside its label, so the status reads before the word does: Zzz for the
 * sleeper berth, a steering wheel for driving, a hard hat on duty, a pause off duty, a forklift for a yard move, and a
 * car for personal conveyance, the driver using the truck as their own vehicle.
 */
const DUTY_STATUS_ICONS: Record<DriverDutyStatusFilter, React.ReactElement> = {
  sleeperBed: <MdiSleep />,
  driving: <MdiSteering />,
  onDuty: <MdiHardHat />,
  offDuty: <PauseCircleOutlineIcon fontSize="inherit" />,
  yardMove: <ForkliftIcon fontSize="inherit" />,
  personalConveyance: <DirectionsCarOutlinedIcon fontSize="inherit" />,
  unknown: <HelpOutlineIcon fontSize="inherit" />,
};

/** Whether a row's duty status is one the icons know; an unrecognized one reads as unknown. */
function isDutyStatus(status: string): status is DriverDutyStatusFilter {
  return (DRIVER_DUTY_STATUS_FILTERS as readonly string[]).includes(status);
}

/**
 * The HOS section: the driver's duty status beside every clock's time left, over the hours coming back.
 *
 * Three columns: the duty status in the first, two rows tall, with its icon and when it changed, off the driver's row
 * as the table has it; the clocks in the other two, 8H Rest and 11H Drive over 14H Shift and 70H Cycle, each with its
 * time left as the section's hero value and whether it is running, "running" in the accent the route gives its next
 * stop. A clock the timer does not carry leaves its cell empty. None is marked as the one that stops the driver
 * first: beside the others, the mark read as the only clock running. The server counts running clocks down to the
 * read, so they are shown as served. A recap line under them names the hours returning at the start of each of the
 * next two days.
 *
 * `undefined` (the first read still in flight) renders the duty status alone, so the section holds its place without
 * waiting; `null` adds "No HOS data for this driver" under it.
 */
export default function HosBlock({
  hos,
  driver,
}: {
  hos: DriverHosSummary | null | undefined;
  driver: Pick<DriverRow, "mostRecentDutyStatusStatus" | "mostRecentDutyStatusChangedAt">;
}) {
  const hasClocks = hos != null && hos.clocks.length > 0;
  const recap = hos ? formatRecap(hos.recap) : null;
  return (
    <ContextSection icon={<TimerOutlinedIcon sx={{ fontSize: 18 }} />} title="HOS" testId={TestIds.dispatch.contextHos}>
      <Box
        sx={{ display: "grid", gridTemplateColumns: hasClocks ? "minmax(0, 1fr) minmax(0, 2fr)" : "minmax(0, 1fr)" }}
      >
        <Box sx={{ borderRight: hasClocks ? `1px solid ${zinc[800]}` : undefined }}>
          <DutyStatus driver={driver} noHos={hos === null} />
        </Box>
        {hasClocks && (
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            {hos.clocks.map((clock) => (
              <ClockCell key={clock.kind} clock={clock} />
            ))}
          </Box>
        )}
      </Box>
      {recap && (
        <ContextText
          variant="subtext"
          testId={TestIds.dispatch.contextHosRecap}
          sx={{ px: 1.5, py: 1, borderTop: `1px solid ${zinc[800]}` }}
        >
          {recap}
        </ContextText>
      )}
    </ContextSection>
  );
}

/** The duty status cell: the status with its icon, when it changed, and that the driver has no timer, if so. */
function DutyStatus({
  driver,
  noHos,
}: {
  driver: Pick<DriverRow, "mostRecentDutyStatusStatus" | "mostRecentDutyStatusChangedAt">;
  noHos: boolean;
}) {
  const status = driver.mostRecentDutyStatusStatus;
  const changed = changedAt(driver.mostRecentDutyStatusChangedAt);
  return (
    <ContextField label="Duty status" testId={TestIds.dispatch.contextHosDutyStatus}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        {status && (
          <Box component="span" aria-hidden sx={{ display: "inline-flex", flex: "none", fontSize: 18, color: "white" }}>
            {isDutyStatus(status) ? DUTY_STATUS_ICONS[status] : DUTY_STATUS_ICONS.unknown}
          </Box>
        )}
        <ContextText variant="value">{formatStatusLabel(status) || NO_VALUE}</ContextText>
      </Box>
      {changed && <ContextText variant="subtext">{changed}</ContextText>}
      {noHos && <ContextText variant="subtext">No HOS data for this driver</ContextText>}
    </ContextField>
  );
}

/**
 * One clock in its fixed cell of the 2x2: its name, the time left on it, and whether it is running.
 *
 * Placed by its kind rather than by the clocks present, so 11H Drive stays top right when the timer carries no 8H Rest.
 */
function ClockCell({ clock }: { clock: HosClockSummary }) {
  const position = HOS_CLOCK_ORDER.indexOf(clock.kind);
  const column = (position % 2) + 1;
  const row = Math.floor(position / 2) + 1;
  return (
    <Box
      component="li"
      data-sb={TestIds.dispatch.contextHosClock}
      sx={{
        gridColumn: column,
        gridRow: row,
        px: 1.25,
        py: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        borderLeft: column === 2 ? `1px solid ${zinc[800]}` : undefined,
        borderTop: row === 2 ? `1px solid ${zinc[800]}` : undefined,
      }}
    >
      <ContextCaption>{HOS_CLOCK_LABELS[clock.kind]}</ContextCaption>
      <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: 0.75 }}>
        <ContextText variant="hero" component="span">
          {formatHosRemaining(clock.secondsLeft)}
        </ContextText>
        <ContextText variant="subtext" component="span" sx={clock.running ? { color: CONTEXT_ACCENT } : undefined}>
          {clock.running ? "running" : "paused"}
        </ContextText>
      </Box>
    </Box>
  );
}
